/**
 * Request lifecycle.
 * - `draft` → `submitted` by the author (field capture done).
 * - `submitted` → `in_review` when the office picks it up.
 * - `in_review` → `converted` once an offer, project or invoice is created
 *   from it; `declined` (we won't do it) or `cancelled` (customer withdrew)
 *   close it with a reason. Closed requests can be reopened for review.
 */
export const REQUEST_STATUSES = [
  "draft",
  "submitted",
  "in_review",
  "converted",
  "declined",
  "cancelled",
] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];
export const REQUEST_CLOSED_STATUSES: readonly RequestStatus[] = [
  "converted",
  "declined",
  "cancelled",
];

/**
 * Offer lifecycle. Only drafts are editable. Sending freezes the content,
 * snapshot and PDF; changes after sending create a new revision and mark the
 * sent one `superseded`. Sent offers past their validity become `expired`.
 */
export const OFFER_STATUSES = [
  "draft",
  "sent",
  "accepted",
  "declined",
  "expired",
  "superseded",
] as const;
export type OfferStatus = (typeof OFFER_STATUSES)[number];

/** Project lifecycle. `completed` and `cancelled` are terminal. */
export const PROJECT_STATUSES = [
  "planned",
  "active",
  "on_hold",
  "completed",
  "cancelled",
] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];
export const PROJECT_TERMINAL_STATUSES: readonly ProjectStatus[] = [
  "completed",
  "cancelled",
];

/** Fixed project-scoped role names; capability sets are tenant-configurable. */
export const PROJECT_MEMBER_ROLES = ["manager", "member"] as const;
export type ProjectMemberRole = (typeof PROJECT_MEMBER_ROLES)[number];

/** Per-project capabilities granted through role membership or global permissions. */
export const PROJECT_CAPABILITIES = [
  "view",
  "edit",
  "manage_members",
  "view_planning",
  "manage_planning",
  "view_costs",
  "manage_costs",
  "link_documents",
  "record_time",
  "manage_inventory",
] as const;
export type ProjectCapability = (typeof PROJECT_CAPABILITIES)[number];

/** Inventory reservation lifecycle. */
export const INVENTORY_RESERVATION_STATUSES = [
  "active",
  "fulfilled",
  "released",
] as const;
export type InventoryReservationStatus =
  (typeof INVENTORY_RESERVATION_STATUSES)[number];

/** Append-only inventory movement ledger types; quantity is always positive. */
export const INVENTORY_MOVEMENT_TYPES = [
  "receipt",
  "adjustment_in",
  "adjustment_out",
  "transfer_in",
  "transfer_out",
  "issue",
  "return",
] as const;
export type InventoryMovementType = (typeof INVENTORY_MOVEMENT_TYPES)[number];

/**
 * Invoice document status. Drafts carry no number and can be deleted.
 * Issuing assigns the number and freezes content; `sent` records delivery.
 * Cancelling is only possible while nothing was paid or credited —
 * otherwise a credit note corrects the invoice.
 */
export const INVOICE_STATUSES = [
  "draft",
  "issued",
  "sent",
  "cancelled",
] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const INVOICE_KINDS = ["invoice", "credit_note"] as const;
export type InvoiceKind = (typeof INVOICE_KINDS)[number];

/** Settlement state derived from payments and credit notes. */
export const PAYMENT_STATUSES = [
  "unpaid",
  "partially_paid",
  "paid",
  "overpaid",
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const PRIORITIES = ["low", "normal", "high"] as const;
export type Priority = (typeof PRIORITIES)[number];

export const PRICING_PREFERENCES = [
  "offer_required",
  "fixed_price",
  "time_and_material",
] as const;
export type PricingPreference = (typeof PRICING_PREFERENCES)[number];

export const SCHEDULING_PREFERENCES = ["asap", "desired_date"] as const;
export type SchedulingPreference = (typeof SCHEDULING_PREFERENCES)[number];

export const LINE_ITEM_TYPES = [
  "work",
  "product",
  "material",
  "flat_rate",
  "text",
  "discount",
] as const;
export type LineItemType = (typeof LINE_ITEM_TYPES)[number];

export const CUSTOMER_TYPES = ["private", "company"] as const;
export type CustomerType = (typeof CUSTOMER_TYPES)[number];

export const CURRENCIES = ["CHF", "EUR"] as const;
export type Currency = (typeof CURRENCIES)[number];

export const USER_ACCOUNT_STATUSES = [
  "invited",
  "active",
  "suspended",
  "deactivated",
] as const;
export type UserAccountStatus = (typeof USER_ACCOUNT_STATUSES)[number];

/** Swiss QR-bill reference types (SIX IG). */
export const QR_REFERENCE_TYPES = ["NON", "SCOR", "QRR"] as const;
export type QrReferenceType = (typeof QR_REFERENCE_TYPES)[number];

export const PAYMENT_METHODS = [
  "bank_transfer",
  "cash",
  "card",
  "twint",
  "other",
] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/**
 * Allowed status transitions. Keys = current status, values = reachable set.
 * Enforced server-side; the UI mirrors this to disable impossible actions.
 */
export const REQUEST_TRANSITIONS: Record<
  RequestStatus,
  readonly RequestStatus[]
> = {
  draft: ["submitted", "cancelled"],
  submitted: ["in_review", "converted", "declined", "cancelled"],
  in_review: ["converted", "declined", "cancelled"],
  converted: ["in_review"],
  declined: ["in_review"],
  cancelled: ["in_review"],
};

export const OFFER_TRANSITIONS: Record<OfferStatus, readonly OfferStatus[]> = {
  draft: ["sent"],
  sent: ["accepted", "declined", "expired", "superseded"],
  expired: ["accepted", "declined", "superseded"],
  accepted: [],
  declined: ["superseded"],
  superseded: [],
};

export const PROJECT_TRANSITIONS: Record<
  ProjectStatus,
  readonly ProjectStatus[]
> = {
  planned: ["active", "on_hold", "cancelled"],
  active: ["on_hold", "completed", "cancelled"],
  on_hold: ["active", "completed", "cancelled"],
  completed: [],
  cancelled: [],
};

export const INVOICE_TRANSITIONS: Record<
  InvoiceStatus,
  readonly InvoiceStatus[]
> = {
  draft: ["issued"],
  issued: ["sent", "cancelled"],
  sent: ["cancelled"],
  cancelled: [],
};

import Decimal from "decimal.js-light";

export interface Settlement {
  paymentStatus: PaymentStatus;
  /** Payments received. */
  paid: string;
  /** Sum of issued credit notes against this invoice. */
  credited: string;
  /** total − credited − paid; negative when overpaid. */
  balance: string;
  /** Open balance past the due date. */
  isOverdue: boolean;
}

/**
 * Derive the settlement of an invoice from its total, payments and credit
 * notes. Overdue is independent of the amount state so a partially paid
 * invoice past its due date is both `partially_paid` and overdue.
 */
export function deriveSettlement(input: {
  total: string | number;
  paid: string | number;
  credited?: string | number;
  dueDate: Date | string | null;
  status: InvoiceStatus;
  kind?: InvoiceKind;
  today: string;
}): Settlement {
  const total = new Decimal(input.total);
  const paid = new Decimal(input.paid);
  const credited = new Decimal(input.credited ?? 0);
  const due = total.minus(credited);
  const balance = due.minus(paid);
  const open =
    (input.kind ?? "invoice") === "invoice" &&
    (input.status === "issued" || input.status === "sent");
  let paymentStatus: PaymentStatus = "unpaid";
  if (open) {
    if (balance.lt(0)) paymentStatus = "overpaid";
    else if (balance.isZero() && (paid.gt(0) || credited.gt(0)))
      paymentStatus = "paid";
    else if (paid.gt(0)) paymentStatus = "partially_paid";
  }
  const dueDate =
    input.dueDate instanceof Date
      ? input.dueDate.toISOString().slice(0, 10)
      : input.dueDate;
  return {
    paymentStatus,
    paid: paid.toFixed(2),
    credited: credited.toFixed(2),
    balance: balance.toFixed(2),
    isOverdue:
      open && balance.gt(0) && dueDate !== null && dueDate < input.today,
  };
}

export function canTransition<T extends string>(
  table: Record<T, readonly T[]>,
  from: T,
  to: T,
): boolean {
  return table[from].includes(to);
}

export const OFFER_EDITABLE_STATUSES: readonly OfferStatus[] = ["draft"];
/** Sent/expired/declined offers can be revised into a new draft revision. */
export const OFFER_REVISABLE_STATUSES: readonly OfferStatus[] = [
  "sent",
  "expired",
  "declined",
];
export const INVOICE_EDITABLE_STATUSES: readonly InvoiceStatus[] = ["draft"];
export const REQUEST_EDITABLE_STATUSES: readonly RequestStatus[] = [
  "draft",
  "submitted",
  "in_review",
];
